import MyRequestsInbox from "../../request/MyRequestsInbox";
import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./dashboard.css";
import DashboardMenu from "./dashboardMenu";
import { getDashboardSummary } from "../../../api/dashboard";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { toast } from "sonner";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";

const AdminDashboard = () => {
  const { selectedYear } = useAcademicYear();

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async () => {
    try {
      if (!selectedYear) return;

      const response = await getDashboardSummary(selectedYear.id);
      if (response?.success) {
        setDashboardData(response.data);
      } else {
        setError(
          response?.message ||
            "Impossible de charger les métriques du tableau de bord.",
        );
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [selectedYear]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <DashboardMenu />
          <MyRequestsInbox />
          {loading && (
            <div className="alert alert-info">Chargement des métriques...</div>
          )}

          {error && !loading && (
            <div className="alert alert-danger">{error}</div>
          )}

          {!loading && !error && dashboardData && (
            <>
              <div className="dashboard-hero card mb-4">
                <div className="row g-0 align-items-center">
                  <div className="col-xl-6 p-4 hero-copy-column">
                    <div className="hero-logo-watermark" aria-hidden="true">
                      <ImageWithBasePath
                        src="assets/img/logo_uie.png"
                        alt="Université UIE"
                        className="hero-logo-watermark-image"
                      />
                    </div>
                    <span className="badge bg-soft-primary mb-3">
                      Interface d’administration
                    </span>
                    <h1 className="hero-title mb-3">
                      Bienvenue sur CAMPUS UIE
                    </h1>
                    <p className="hero-text mb-4">
                      Pilotez l'université avec une vue claire des effectifs,
                      des enseignants, des parents et des infrastructures.
                    </p>
                    <div className="hero-badges d-flex flex-wrap gap-2">
                      <span className="hero-badge">
                        Année active {dashboardData.academicYear.label}
                      </span>
                      <span className="hero-badge">
                        {dashboardData.classes} classes
                      </span>
                    </div>
                  </div>
                  <div className="col-xl-6 d-flex align-items-center justify-content-center p-4">
                    <div className="hero-visual-box">
                      <ImageWithBasePath
                        src="UIE_tof.png"
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-lg-4 col-sm-6">
                  <div className="card dashboard-card dashboard-card-primary">
                    <div className="card-body d-flex align-items-centre justify-content-between">
                      <div className="d-flex flex-column align-items-start justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Étudiants</span>
                          <h2 className="dashboard-value">
                            {dashboardData.students.total}
                          </h2>
                        </div>
                        <p className="dashboard-meta mb-0">
                          {dashboardData.students.female} filles •{" "}
                          {dashboardData.students.male} garçons
                        </p>
                      </div>
                      <ImageWithBasePath
                        src="assets/img/icons/student.svg"
                        width={130}
                        height={130}
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-sm-6 ">
                  <div className="card dashboard-card dashboard-card-success ">
                    <div className="card-body d-flex align-items-center justify-content-between ">
                      <div className="d-flex flex-column align-items-start justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Professeurs</span>
                          <h2 className="dashboard-value">
                            {dashboardData.teachers}
                          </h2>
                        </div>
                        <p className="dashboard-meta mb-0">
                          Enseignants en poste
                        </p>
                      </div>
                      <ImageWithBasePath
                        src="assets/img/icons/teacher.svg"
                        width={130}
                        height={130}
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-sm-6">
                  <div className="card dashboard-card dashboard-card-warning">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div className="d-flex flex-column align-items-start justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Parents</span>
                          <h2 className="dashboard-value">
                            {dashboardData.parents}
                          </h2>
                        </div>
                        <p className="dashboard-meta mb-0">
                          Comptes parents actifs
                        </p>
                      </div>
                      <ImageWithBasePath
                        src="assets/img/icons/parent.webp"
                        width={130}
                        height={130}
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-xl-4 col-md-6">
                  <div className="card dashboard-card dashboard-card-info">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div className="d-flex flex-column align-items-start justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Classes</span>
                          <h2 className="dashboard-value">
                            {dashboardData.classes}
                          </h2>
                        </div>
                        <p className="dashboard-meta mb-0">
                          Structures pédagogiques
                        </p>
                      </div>
                      <ImageWithBasePath
                        src="assets/img/icons/class.png"
                        width={130}
                        height={130}
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-md-6">
                  <div className="card dashboard-card dashboard-card-secondary">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div className="d-flex flex-column align-items-start justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Salles</span>
                          <h2 className="dashboard-value">
                            {dashboardData.classrooms}
                          </h2>
                        </div>
                        <p className="dashboard-meta mb-0">
                          Capacités disponibles
                        </p>
                      </div>
                      <ImageWithBasePath
                        src="assets/img/icons/classroom.png"
                        width={120}
                        height={120}
                        alt="Université UIE"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-md-12">
                  <div className="card dashboard-card dashboard-card-dark">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <span className="dashboard-label">Synthèse</span>
                          <h2 className="dashboard-value">
                            {dashboardData.students.total +
                              dashboardData.teachers +
                              dashboardData.parents}
                          </h2>
                        </div>
                        <span className="dashboard-card-icon bg-dark">
                          <i className="ti ti-chart-pie" />
                        </span>
                      </div>
                      <p className="dashboard-meta mb-0">
                        Indicateur clé de gestion
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
